/**
 * 外部ナビ（Googleマップ）への受け渡し。
 *
 * このアプリが計算するのは「どの経路がどれだけ日焼けするか」であって、
 * 曲がり角の案内はしない。実際のナビはGoogleマップに任せ、こちらは
 * 日陰ルートを経由地としてURLに載せて渡す（Google Maps URLs API）。
 *
 * 電車・自動車の経路探索そのものは行わない。地形タイルは歩行グリッドであり、
 * 道路網の方向規制も鉄道の時刻表も持たないため、in-appで計算すると
 * 実際と食い違う。これらはGoogleマップへ委譲する（§13の姿勢：できないことは正直に）。
 */
import type { LatLon } from "./types.js";

export type TravelMode = "walking" | "transit" | "driving";

export const TRAVEL_MODE_LABEL: Record<TravelMode, string> = {
  walking: "徒歩",
  transit: "電車",
  driving: "自動車",
};

/** Google Maps URLs API の経由地上限（api=1 では9件まで）。端点を除いた本数 */
export const MAX_WAYPOINTS = 8;

/**
 * 経路点列を経由地として使える本数まで間引く。
 * 端点（出発地・目的地）はURLのorigin/destinationで渡すため含めない。
 * 等間隔サンプリングなので、日陰ルートの「どこを通るか」がおおよそ保たれる。
 */
export function downsampleWaypoints(path: LatLon[], max = MAX_WAYPOINTS): LatLon[] {
  const inner = path.slice(1, -1);
  if (inner.length === 0) return [];
  if (inner.length <= max) return inner;
  const out: LatLon[] = [];
  for (let i = 0; i < max; i++) {
    // 区間の中央を代表点にして端に寄らないようにする
    const idx = Math.round(((i + 0.5) * inner.length) / max - 0.5);
    out.push(inner[Math.min(inner.length - 1, Math.max(0, idx))]!);
  }
  return out;
}

function coord(p: LatLon): string {
  return `${p.lat.toFixed(6)},${p.lon.toFixed(6)}`;
}

export interface DirectionsOptions {
  origin: LatLon;
  destination: LatLon;
  mode: TravelMode;
  /** 徒歩の日陰ルートを再現させたいときだけ渡す（電車・自動車では無視される） */
  waypoints?: LatLon[];
}

/**
 * Googleマップの経路URL。スマホではアプリが開き、そのままナビを開始できる。
 * 経由地は徒歩のときだけ載せる（電車・自動車の経路はGoogle側の最適化に任せる）。
 */
export function googleMapsDirectionsUrl(opts: DirectionsOptions): string {
  const params = new URLSearchParams({
    api: "1",
    origin: coord(opts.origin),
    destination: coord(opts.destination),
    travelmode: opts.mode,
  });
  if (opts.mode === "walking" && opts.waypoints && opts.waypoints.length > 0) {
    params.set("waypoints", opts.waypoints.slice(0, MAX_WAYPOINTS).map(coord).join("|"));
  }
  return `https://www.google.com/maps/dir/?${params}`;
}

/**
 * その移動手段で、アプリ内の日陰ルート比較が意味を持つか。
 * 徒歩のみ true（電車・自動車は屋内・車内が主で、日焼けは徒歩区間で起きる）。
 */
export function supportsShadeRouting(mode: TravelMode): boolean {
  return mode === "walking";
}

/** 電車・自動車を選んだときの説明文（できないことを明示する） */
export const NON_WALKING_NOTE: Record<Exclude<TravelMode, "walking">, string> = {
  transit:
    "電車の経路検索はGoogleマップで行います。日焼けは主に駅までの徒歩区間で起きるため、" +
    "その区間を徒歩モードで調べると日陰ルートを比較できます。",
  driving:
    "自動車の経路検索はGoogleマップで行います。走行中は車内のため日焼けはほとんどありません" +
    "（窓越しのUV-Aは本アプリの対象外です）。",
};
